import { Spacing } from '@persona-id/design-tokens';
import { ComponentProps, ReactNode, useMemo, useRef } from 'react';
import styled from 'styled-components';

import EditorNavItemLink from 'dashboard/components/EditorNavItemLink';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import FormError from 'dashboard/components/FormControls/FormError';
import Form from 'dashboard/layouts/FormLayout/Form';
import { ModalContainer } from 'dashboard/layouts/ModalLayout';
import { ConfigQueryParam } from 'dashboard/routes/DashboardWorkflowsShowRoute/types';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Heading, Label, Paragraph } from 'design-system/components/Text';
import TextArea from 'design-system/components/TextArea';
import usePermission from 'lib/hooks/usePermission';
import { usePreserveQueries } from 'lib/hooks/usePreserveQuery';
import { Permission } from 'lib/permissions';
import { useFormSecurely } from 'lib/security/reactHookForm';

export type EditorNavItemLinkProps = ComponentProps<typeof EditorNavItemLink>;

const StyledModalContainer = styled(ModalContainer)`
  flex-direction: row;
  height: clamp(700px, 85vh, 2700px);
  width: clamp(1020px, 90vw, 1600px);
  padding: 0;
`;

const StyledButton = styled(Button)`
  width: fit-content;
`;

const LeftColumn = styled.div`
  width: 240px;
  padding: ${Spacing[5]};
  background-color: ${({ theme }) => theme.color.background.primary};
  border-right: 1px solid ${({ theme }) => theme.color.border.primary};
  border-top-left-radius: inherit;
  border-bottom-left-radius: inherit;
`;

const RightColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;

  flex: auto;
  border-radius: inherit;
`;

const RightColumn = styled.div`
  flex: auto;
  padding: ${Spacing[5]};
  overflow-y: auto;
  background-color: ${({ theme }) => theme.color.background.secondary};
`;

const StyledModalFooter = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;

  padding: ${Spacing[4]};

  background-color: ${({ theme }) => theme.color.background.primary};
  border-top: 1px solid ${({ theme }) => theme.color.border.primary};
  border-bottom-left-radius: inherit;
  border-bottom-right-radius: inherit;
`;

const FooterParagraph = styled(Paragraph)`
  max-width: 560px;
`;

interface WorkflowConfigModalProps {
  name: string;
  description: string;
  sectionViews: ModalSectionView[];
  versionId: string;
  configQueryParam: ConfigQueryParam;
  handleCloseModal?: () => void;
  onUpdateSettings: (data: { name?: string; description?: string }) => Promise<void>;
}

interface WorkflowSettingsData {
  name: string;
  description: string;
}

export interface ModalSectionView {
  title: string;
  navItem: EditorNavItemLinkProps;
  contents?: ReactNode;
  dataTest?: string;
  onSubmit: () => void;
  isSubmitting: boolean;
  disableSubmit: boolean;
  submitButtonText: string;
  footerText?: string;
  configParam: ConfigQueryParam;
}

const WorkflowConfigModal = ({
  description,
  name,
  sectionViews,
  versionId,
  configQueryParam,
  handleCloseModal,
  onUpdateSettings,
}: WorkflowConfigModalProps) => {
  const rightColumnRef = useRef<HTMLDivElement>(null);
  const hasWritePermission = usePermission(Permission.WorkflowWrite);
  const preserveQueries = usePreserveQueries();

  const {
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    register,
    reset,
  } = useFormSecurely<WorkflowSettingsData>({
    defaultValues: {
      name: name,
      description: description,
    },
  });

  const handleSettingsSubmit = handleSubmit(async (data: WorkflowSettingsData) => {
    await onUpdateSettings({ ...data });
    reset(data);
  });

  const settingsNavItem: EditorNavItemLinkProps = useMemo(
    () => ({
      id: 'settings',
      label: 'Name & Description',
      route: {
        pathname: '.',
        search: preserveQueries({ version: versionId, config: 'settings' }),
      },
    }),
    [versionId, preserveQueries],
  );

  const settingsSection: ModalSectionView = useMemo(
    () => ({
      title: settingsNavItem.label,
      navItem: settingsNavItem,
      configParam: 'settings',
      dataTest: 'workflows-config-modal-nav__general',
      contents: (
        <Form>
          <FormControlLabel htmlFor='workflow-settings__name'>Name</FormControlLabel>
          <Input
            id='workflow-settings__name'
            disabled={!hasWritePermission}
            hasError={!!errors.name}
            {...register('name', {
              required: 'Please provide a name',
              maxLength: { value: 255, message: 'Name must not exceed 255 characters' },
            })}
          />
          {errors.name && <FormError>{errors.name.message}</FormError>}
          <Spacer height={2} />
          <FormControlLabel htmlFor='workflow-settings__description'>Description</FormControlLabel>
          <TextArea
            id='workflow-settings__description'
            disabled={!hasWritePermission}
            placeholder='Optional description...'
            {...register('description')}
          />
        </Form>
      ),
      onSubmit: handleSettingsSubmit,
      isSubmitting: isSubmitting,
      disableSubmit: !hasWritePermission || !isDirty,
      submitButtonText: 'Save',
    }),
    [
      settingsNavItem,
      handleSettingsSubmit,
      isSubmitting,
      hasWritePermission,
      isDirty,
      errors.name,
      register,
    ],
  );

  const childrenToRender: ModalSectionView[] = useMemo(
    () => [settingsSection, ...sectionViews],
    [settingsSection, sectionViews],
  );
  const currentChild = useMemo(
    () => childrenToRender.find(({ configParam }) => configParam === configQueryParam),
    [childrenToRender, configQueryParam],
  );

  if (!currentChild) {
    return null;
  }

  return (
    <StyledModalContainer data-test='workflows-config-modal'>
      <LeftColumn>
        <Label size='sm' color='tertiary'>
          Configurations
        </Label>
        {childrenToRender.map(({ navItem }) => (
          <EditorNavItemLink
            key={navItem.id}
            id={navItem.id}
            label={navItem.label}
            route={navItem.route}
          />
        ))}
      </LeftColumn>
      <RightColumnWrapper>
        <RightColumn ref={rightColumnRef}>
          <Heading size='sm' data-test={currentChild.dataTest}>
            {currentChild.title}
          </Heading>
          <Spacer height={3} />
          <Card>
            <Card.Body>{currentChild.contents}</Card.Body>
          </Card>
        </RightColumn>
        <StyledModalFooter data-test='workflows-config-modal__footer'>
          <HStack spacing={Spacing[2]}>
            <StyledButton
              id='workflows-config-modal__close-button'
              variant='basic'
              onClick={handleCloseModal}
            >
              Cancel
            </StyledButton>
            <StyledButton
              id='workflows-config-modal__save-button'
              variant='primary'
              isPending={currentChild.isSubmitting}
              disabled={currentChild.disableSubmit}
              onClick={currentChild.onSubmit}
            >
              {currentChild.submitButtonText}
            </StyledButton>
          </HStack>
          {currentChild.footerText && (
            <FooterParagraph size='sm'>{currentChild.footerText}</FooterParagraph>
          )}
        </StyledModalFooter>
      </RightColumnWrapper>
    </StyledModalContainer>
  );
};

export default WorkflowConfigModal;
